/**
 * Danh sách ứng dụng mặc định (fallback khi Firebase chưa có dữ liệu).
 * Admin có thể chỉnh sửa từng mục trong trang quản trị — khi lưu, bản ghi
 * sẽ được đẩy lên Firebase và bản seed này sẽ bị ghi đè bởi bản trên DB.
 *
 * Schema xem tại `src/utils/appSchema.js`.
 *
 * Nội dung mô tả được đối chiếu (29/08/2026) với README + mô tả repo trên
 * github.com/TuanBT, và với <title>/<meta> của chính trang đang chạy.
 * Chỗ nào không kiểm chứng được thì để trống thay vì đoán.
 */

export const seedApps = [
    {
        id: 'biensoxe',
        title: 'Biển số xe',
        tagline: 'Biển số này của tỉnh nào? Tra trên bản đồ',
        description: 'Tra cứu đầu số biển số xe của các tỉnh thành Việt Nam trên bản đồ tương tác',
        longDescription:
            'Rê chuột hay bấm vào một tỉnh trên bản đồ, tỉnh đó sáng lên kèm đầu số biển số và thông tin của tỉnh. ' +
            'Ngược lại, chọn một đầu số trong danh sách thì bản đồ tự sáng đúng vị trí tương ứng.\n' +
            'Bản đồ là một file SVG 64 tỉnh, toàn bộ dữ liệu nằm sẵn trong trang nên tra cứu không cần gọi mạng.',
        path: 'biensoxe',
        selfHosted: true,
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        featured: true,
        tags: ['javascript', 'jquery', 'svg', 'map'],
        features: [
            'Bản đồ SVG 64 tỉnh, sáng lên khi rê chuột hoặc bấm',
            'Tra được hai chiều: từ tỉnh ra đầu số và ngược lại',
            'Dữ liệu nằm sẵn trong trang, tra cứu không cần gọi mạng',
        ],
        techStack: ['JavaScript', 'jQuery', 'SVG'],
        date: '2024-01',
        thumbnail: '/biensoxe/thumbnail.webp',
        images: ['/biensoxe/thumbnail.webp'],
    },
    {
        id: 'note',
        title: 'Tuan Note',
        tagline: 'Mở lên là viết được ngay',
        description: 'Ứng dụng ghi chú tối giản, đồng bộ qua Firebase, có tìm kiếm và chế độ tối',
        longDescription:
            'Ghi chú không cần thủ tục: mở trang lên là viết. Nội dung lưu qua Firebase nên ghi ở máy này ' +
            'là thấy ở máy khác. Có ô tìm kiếm lọc theo nội dung, xoá và sửa từng ghi chú, ' +
            'kèm chế độ sáng và tối.',
        path: 'note',
        selfHosted: true,
        category: 'Quản lý',
        platform: 'Web',
        status: 'live',
        tags: ['firebase', 'notes', 'dark-mode'],
        features: [
            'Đồng bộ qua Firebase giữa các thiết bị',
            'Tìm kiếm lọc theo nội dung ghi chú',
            'Sửa, xoá từng ghi chú',
            'Chế độ sáng và tối',
        ],
        techStack: ['JavaScript', 'Firebase'],
        date: '2024-02',
        thumbnail: '/note/thumbnail.png',
        images: ['/note/thumbnail.png'],
    },
    {
        id: 'caro',
        title: 'Game Caro',
        tagline: 'Bàn cờ caro cho hai người, có bấm giờ',
        description: 'Trò cờ caro chơi hai người trên cùng một máy, có đồng hồ đếm thời gian ván đấu',
        longDescription:
            'Bàn cờ caro cổ điển để hai người chơi trên cùng một màn hình. ' +
            'Bàn cờ tự co giãn theo kích thước cửa sổ và có đồng hồ đếm thời gian ván đấu.',
        path: 'caro',
        selfHosted: true,
        category: 'Game',
        platform: 'Web',
        status: 'live',
        tags: ['game', 'jquery', 'caro'],
        features: [
            'Hai người chơi trên cùng một máy',
            'Bàn cờ tự co giãn theo cửa sổ',
            'Đồng hồ đếm thời gian ván đấu',
        ],
        techStack: ['JavaScript', 'jQuery'],
        date: '2023-12',
        thumbnail: '/caro/thumbnail.png',
        images: ['/caro/thumbnail.png'],
    },
    {
        id: 'clock',
        title: 'Alarm Clock',
        tagline: 'Đồng hồ báo thức chạy ngay trên trình duyệt',
        description: 'Đồng hồ và báo thức dùng khi cần một cái đồng hồ to trên màn hình phụ',
        longDescription:
            'Một chiếc đồng hồ web để bật toàn màn hình trên màn hình phụ, kèm chức năng báo thức. ' +
            'Không cần cài app, không cần đăng nhập — mở tab lên là dùng.',
        path: 'clock',
        selfHosted: true,
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        tags: ['clock', 'alarm', 'time'],
        features: [
            'Xem giờ và đặt báo thức ngay trên tab trình duyệt',
            'Hiển thị lớn, hợp để bật toàn màn hình',
        ],
        techStack: ['JavaScript', 'CSS'],
        date: '2023-11',
        thumbnail: '/clock/thumbnail.webp',
        images: ['/clock/thumbnail.webp'],
    },
    {
        id: 'exam',
        title: 'Exam',
        tagline: 'Tổ chức thi trắc nghiệm, chấm điểm tự động',
        description: 'Ứng dụng thi trắc nghiệm online có bấm giờ và chấm điểm ngay khi nộp bài',
        longDescription:
            'Tổ chức một kỳ thi trắc nghiệm trực tuyến: ra đề, đặt thời gian làm bài, ' +
            'và chấm điểm tự động ngay khi thí sinh nộp.',
        path: 'exam',
        category: 'Quản lý',
        platform: 'Web',
        status: 'live',
        tags: ['quiz', 'education', 'timer'],
        features: [
            'Bấm giờ làm bài và tự nộp khi hết giờ',
            'Chấm điểm tự động ngay khi nộp',
        ],
        techStack: ['JavaScript'],
        date: '2023-10',
        thumbnail: '/exam/thumbnail.webp',
        images: ['/exam/thumbnail.webp'],
        externalUrl: 'https://exam.buitientuan.com/',
        githubUrl: 'https://github.com/TuanBT/exam',
    },
    {
        id: 'hotkey',
        title: 'Hotkey',
        tagline: 'Quên phím tắt thì tra ở đây',
        description: 'Bảng tra phím tắt của các ứng dụng hay dùng, xem theo từng ứng dụng',
        longDescription:
            'Một bảng tra cứu phím tắt: chọn ứng dụng ở danh sách bên trái — Chrome, Firefox, Finder, ' +
            'Skype, Text Edit… — và xem tổ hợp phím cho từng chức năng.',
        path: 'hotkey',
        selfHosted: true,
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        tags: ['hotkey', 'keyboard', 'reference'],
        features: [
            'Xem phím tắt theo từng ứng dụng',
            'Có icon nhận diện từng ứng dụng',
        ],
        techStack: ['JavaScript', 'jQuery'],
        date: '2023-08',
        thumbnail: '/hotkey/thumbnail.webp',
        images: ['/hotkey/thumbnail.webp'],
    },
    {
        id: 'masoi',
        title: 'Ma Sói',
        tagline: 'Ma Sói với hội bạn, ngay trên trình duyệt',
        description: 'Trò chơi Ma Sói online: tạo phòng, chia vai và quản trò ngay trên trình duyệt',
        longDescription:
            'Bản online của trò Ma Sói. Tạo phòng, gửi mã cho bạn bè, rồi cả nhóm chơi ngay trên trình duyệt — ' +
            'không cần cài gì. Hệ thống lo phần chia vai và dẫn dắt các pha đêm, ngày, bỏ phiếu, ' +
            'nên không cần một người ngồi ngoài làm quản trò.',
        path: 'masoi',
        category: 'Game',
        platform: 'Web',
        status: 'live',
        featured: true,
        tags: ['multiplayer', 'realtime', 'social', 'game'],
        features: [
            'Tạo phòng và mời bạn bằng mã phòng',
            'Tự chia vai và dẫn dắt các pha đêm, ngày, bỏ phiếu',
            'Không cần người làm quản trò riêng',
        ],
        techStack: ['JavaScript', 'Firebase'],
        date: '2023-07',
        thumbnail: '/masoi/thumbnail.webp',
        images: ['/masoi/thumbnail.webp'],
        externalUrl: 'https://masoi.buitientuan.com/',
    },
    {
        id: 'beercircle',
        title: 'Beer Circle',
        tagline: 'Vòng quay may mắn cho mọi cuộc vui',
        description: 'Vòng quay chọn ngẫu nhiên — để bàn nhậu khỏi cãi nhau xem tới lượt ai',
        longDescription:
            'Nhập danh sách tên rồi quay. Vòng quay vẽ bằng Canvas, quay xong dừng ở một cái tên — ' +
            'dùng để chọn ai uống, ai trả tiền, hay ai phải hát. Không cần cài gì, mở trình duyệt là chạy.',
        path: 'beercircle',
        selfHosted: true,
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        tags: ['canvas', 'random', 'wheel', 'fun'],
        features: [
            'Vòng quay Canvas, tự nhập danh sách lựa chọn',
            'Kết quả ngẫu nhiên, quay lại được nhiều lần',
            'Chạy thẳng trên trình duyệt, không cần cài đặt',
        ],
        techStack: ['JavaScript', 'Canvas'],
        date: '2023-06',
        thumbnail: '/beercircle/thumbnail.webp',
        images: ['/beercircle/thumbnail.webp'],
    },
    {
        id: 'cocvuong',
        title: 'Cóc Vương',
        tagline: 'Chấm điểm và điều hành giải võ',
        description: 'Phần mềm chấm điểm cho giải võ thuật: giám định bấm điểm, tự xếp hạng và chia cặp thi đấu',
        longDescription:
            'Hệ thống điều hành một giải võ từ đầu tới cuối. Nhiều giám định cùng bấm điểm trên thiết bị riêng, ' +
            'hệ thống chỉ ghi nhận khi quá nửa số giám định cùng chấm, nên tránh được điểm lệch do một người bấm nhầm. ' +
            'Với nội dung quyền, điểm cao nhất và thấp nhất bị loại trước khi tính tổng.\n' +
            'Ngoài phần chấm còn có bốc thăm thứ tự thi đấu, tự dựng bảng chia cặp cho nội dung đối kháng ' +
            'và xuất dữ liệu kết quả. Bản hiện tại viết bằng React + TypeScript, kế thừa từ hai bản C# đời trước.',
        path: 'cocvuong',
        category: 'Quản lý',
        platform: 'Web',
        status: 'live',
        featured: true,
        tags: ['react', 'typescript', 'scoring', 'tournament', 'vovinam'],
        features: [
            'Nhiều giám định chấm song song, chỉ ghi điểm khi quá bán cùng bấm',
            'Nội dung quyền tự loại điểm cao nhất và thấp nhất trước khi tính tổng',
            'Bốc thăm thứ tự và tự dựng bảng chia cặp đối kháng',
            'Xuất dữ liệu kết quả và tự xếp hạng theo từng nội dung',
        ],
        techStack: ['React', 'TypeScript'],
        date: '2023-04',
        thumbnail: '/cocvuong/thumbnail.webp',
        images: ['/cocvuong/thumbnail.webp'],
        externalUrl: 'https://cocvuong.buitientuan.com/',
        githubUrl: 'https://github.com/TuanBT/cocvuong-react',
    },
    {
        id: 'giapha',
        title: 'Gia Phả',
        tagline: 'Dựng và lưu giữ gia phả dòng họ',
        description: 'Hệ thống quản lý gia phả dòng họ',
        longDescription:
            'Dựng và lưu giữ gia phả cho dòng họ. Một tài khoản quản lý được nhiều cây gia phả ' +
            'khác nhau, mỗi cây hiển thị số thành viên và số thế hệ đã ghi.\n' +
            'Gia phả có thể để riêng tư hoặc chia sẻ công khai cho cộng đồng cùng xem; ' +
            'phần công khai có ô tìm kiếm theo tên dòng họ.',
        path: 'giapha',
        category: 'Quản lý',
        platform: 'Web',
        status: 'live',
        tags: ['genealogy', 'family'],
        features: [
            'Quản lý nhiều cây gia phả trong một tài khoản',
            'Đếm số thành viên và số thế hệ của từng cây',
            'Chia sẻ công khai cho cộng đồng, có tìm kiếm theo tên dòng họ',
            'Giao diện sáng và tối',
        ],
        techStack: [],
        date: '2023-02',
        thumbnail: '/giapha/thumbnail.webp',
        images: ['/giapha/thumbnail.webp'],
        externalUrl: 'https://giapha.buitientuan.com/',
    },
    {
        id: 'ongtroioi',
        title: 'Ông Trời Ơi',
        tagline: 'Phân vân thì để ông trời quyết hộ',
        description: 'Công cụ chọn ngẫu nhiên giữa hai lựa chọn, có sẵn chế độ sấp–ngửa và có–không',
        longDescription:
            '“Con hãy nói ba lần: ÔNG TRỜI ƠI, HÃY GIÚP CON” — rồi bấm hỏi, và nhận một câu trả lời ngẫu nhiên.\n' +
            'Có sẵn hai chế độ sấp–ngửa và có–không, hoặc bạn tự gõ hai lựa chọn của riêng mình rồi lưu lại ' +
            'để lần sau khỏi nhập lại.',
        path: 'ongtroioi',
        selfHosted: true,
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        tags: ['random', 'decision', 'fun'],
        features: [
            'Chế độ sấp–ngửa và có–không dựng sẵn',
            'Tự đặt hai lựa chọn của riêng bạn và lưu lại',
            'Chạy hoàn toàn trên trình duyệt',
        ],
        techStack: ['JavaScript', 'CSS'],
        date: '2023-01',
        thumbnail: '/ongtroioi/thumbnail.webp',
        images: ['/ongtroioi/thumbnail.webp'],
        githubUrl: 'https://github.com/TuanBT/ongtroioi',
    },
    {
        id: 'trangtuan',
        title: 'Trang & Tuân',
        tagline: 'Thiệp cưới online của hai đứa',
        description: 'Thiệp cưới điện tử với album ảnh, đếm ngược và bản đồ dẫn đường',
        longDescription:
            'Thiệp cưới điện tử của Trang và Tuân: album ảnh xem được ở dạng phóng to, ' +
            'đồng hồ đếm ngược tới ngày cưới và bản đồ Google Maps dẫn đường tới địa điểm.',
        path: 'trangtuan',
        selfHosted: true,
        category: 'Cá nhân',
        platform: 'Web',
        status: 'live',
        // Đang ẩn trên Firebase — giữ seed khớp để không loé lên rồi biến mất.
        hidden: true,
        tags: ['wedding', 'invitation', 'jquery'],
        features: [
            'Album ảnh xem phóng to dạng lightbox',
            'Đếm ngược tới ngày cưới',
            'Bản đồ Google Maps dẫn đường tới địa điểm',
        ],
        techStack: ['JavaScript', 'jQuery', 'Bootstrap'],
        date: '2018-12',
        thumbnail: '/trangtuan/thumbnail.webp',
        images: ['/trangtuan/thumbnail.webp'],
    },
    {
        id: 'nha48',
        title: 'Nhà 48',
        tagline: 'Thay cuốn sổ ghi tiền trọ và tiền điện nước',
        description: 'Ứng dụng quản lý phòng trọ: hợp đồng, tiền thuê, chỉ số điện nước',
        longDescription:
            'Quản lý danh sách phòng, hợp đồng thuê và ghi chỉ số điện nước hằng tháng để tính hoá đơn từng phòng.',
        path: 'nha48',
        category: 'Quản lý',
        platform: 'Web',
        status: 'wip',
        // Ẩn: nha48.buitientuan.com không còn bản ghi DNS và chưa có repo công khai.
        // Bỏ ẩn trong trang quản trị sau khi điền link chạy được.
        hidden: true,
        tags: ['react', 'firebase', 'rental'],
        features: [],
        techStack: ['React', 'Firebase'],
        date: '2025-01',
        thumbnail: null,
        images: [],
        selfHosted: false,
    },
    {
        id: 'tuan-ai',
        title: 'Tuân AI',
        tagline: 'Hộp thu ý tưởng cho kênh video AI',
        description: 'Nơi người xem gửi ảnh kèm mô tả ý tưởng, chủ kênh duyệt tay rồi dựng clip AI',
        longDescription:
            'Người xem gửi lên một hai tấm ảnh kèm mô tả ý tưởng họ muốn thấy. ' +
            'Chủ kênh vào khu quản trị duyệt từng bài, rồi tự dựng clip và đăng lên kênh. ' +
            'Trang này không tự sinh ảnh hay video — nó chỉ nhận và sắp xếp ý tưởng.\n' +
            'Toàn bộ chạy trọn trong gói miễn phí của Cloudflare, không cần thẻ tín dụng: ' +
            'React và API deploy chung một lần trên Workers, ảnh nằm ở KV, bài gửi và thống kê ở D1, ' +
            'Turnstile chặn bot, và một cron job dọn ảnh quá hạn mỗi đêm.',
        path: 'tuan-ai',
        category: 'Cá nhân',
        platform: 'Web',
        status: 'live',
        tags: ['cloudflare', 'workers', 'react', 'typescript', 'd1'],
        features: [
            'Người xem gửi ảnh kèm mô tả, chủ kênh duyệt tay từng bài',
            'Giới hạn số bài mỗi người mỗi ngày, có Turnstile chặn bot',
            'Cron dọn ảnh quá hạn mỗi đêm',
            'Chạy trọn trong gói miễn phí Cloudflare — Workers, KV và D1',
        ],
        techStack: ['React', 'TypeScript', 'Cloudflare Workers', 'D1', 'Workers KV'],
        date: '2025-06',
        thumbnail: '/tuan-ai/thumbnail.png',
        images: ['/tuan-ai/thumbnail.png'],
        externalUrl: 'https://tuan-ai.tuanbt.workers.dev',
        githubUrl: 'https://github.com/TuanBT/tuan-ai',
    },
    {
        id: 'tradingky',
        title: 'Trading Ký',
        tagline: 'Nhật ký trading: lợi nhuận và cả tâm lý',
        description: 'Ghi lại nhật ký giao dịch, theo dõi lợi nhuận và tâm lý giao dịch qua từng lệnh',
        longDescription:
            'Ghi lại từng lệnh kèm lý do vào lệnh và tâm trạng lúc đó, để về sau nhìn lại thấy được ' +
            'thói quen giao dịch của chính mình — chứ không chỉ thấy con số lời lỗ. ' +
            'Ứng dụng tổng hợp lợi nhuận theo thời gian và theo từng mã.',
        path: 'tradingky',
        category: 'Quản lý',
        platform: 'Web',
        status: 'live',
        tags: ['nextjs', 'typescript', 'trading', 'journal'],
        features: [
            'Nhật ký từng lệnh, kèm lý do vào lệnh và tâm lý lúc giao dịch',
            'Theo dõi lợi nhuận theo thời gian',
        ],
        techStack: ['Next.js', 'TypeScript'],
        date: '2025-03',
        thumbnail: '/tradingky/thumbnail.png',
        images: ['/tradingky/thumbnail.png'],
        externalUrl: 'https://tradingky.vercel.app',
        githubUrl: 'https://github.com/TuanBT/tradingky',
    },
    {
        id: 'split-bill',
        title: 'Split Bill',
        tagline: 'Ăn xong khỏi bấm máy tính — ai nợ ai, tự ra',
        description: 'Ứng dụng chia tiền nhóm, tự tính ai còn nợ ai bao nhiêu',
        longDescription:
            'Nhập từng khoản chi và ai đã trả, ứng dụng tự tính ra ai còn nợ ai bao nhiêu.',
        path: 'split-bill',
        category: 'Tiện ích',
        platform: 'Web',
        status: 'wip',
        // Ẩn: splitbill.buitientuan.com không còn bản ghi DNS và chưa có repo công khai.
        hidden: true,
        tags: ['react', 'firebase', 'split-bill'],
        features: [],
        techStack: ['React', 'Firebase'],
        date: '2024-06',
        thumbnail: null,
        images: [],
        selfHosted: false,
    },
    {
        id: 'sapnhap',
        title: 'Sáp Nhập',
        tagline: 'Xã cũ giờ thuộc về đâu? Tra trên bản đồ',
        description: 'Tra cứu việc sáp nhập đơn vị hành chính Việt Nam 2025 trên bản đồ tương tác',
        longDescription:
            'Đợt sắp xếp đơn vị hành chính 2025 đổi tên và gộp lại rất nhiều xã, phường. ' +
            'Trang này cho tra cứu trên bản đồ: đơn vị cũ giờ thuộc về đâu, tên gọi mới là gì. ' +
            'Tìm được theo cả tên cũ lẫn tên mới.',
        path: 'sapnhap',
        category: 'Tiện ích',
        platform: 'Web',
        status: 'live',
        tags: ['react', 'typescript', 'vite', 'map', 'vietnam'],
        features: [
            'Bản đồ hành chính tương tác',
            'Tìm theo cả tên cũ và tên mới',
        ],
        techStack: ['React', 'TypeScript', 'Vite'],
        date: '2025-07',
        thumbnail: '/sapnhap/thumbnail.png',
        images: ['/sapnhap/thumbnail.png'],
        externalUrl: 'https://sapnhap.buitientuan.com/',
    },
    {
        id: 'macmute',
        title: 'MacMute',
        tagline: 'Một phím tắt để câm mic, ở bất kỳ đâu',
        description: 'Ứng dụng menu bar macOS tắt mic ở mức thiết bị bằng phím tắt toàn cục — dùng được với mọi app họp',
        longDescription:
            'macOS không có phím tắt tắt mic dùng chung cho cả máy. Teams, Zoom, Meet đều có phím tắt riêng, ' +
            'nhưng tất cả cùng một giới hạn: chúng chỉ nghe khi cửa sổ của chính chúng đang ở trước — ' +
            'đúng lúc bạn chuyển sang trình duyệt hay editor thì phím tắt hết tác dụng, mà đó lại chính là ' +
            'lúc bạn cần tắt mic nhất.\n' +
            'MacMute nằm dưới tất cả và trả lời ở bất kỳ đâu. Nó tắt mic ở mức thiết bị, nên một phím tắt ' +
            'phủ luôn mọi ứng dụng: Teams, Zoom, Google Meet, Slack, Discord, FaceTime. ' +
            'Riêng với Teams, nó bấm hộ luôn nút Mute trong app để người khác thấy bạn đã tắt mic, ' +
            'chứ không chỉ im lặng.\n' +
            'Miễn phí, mã nguồn mở theo giấy phép MIT, chạy trên macOS 13 trở lên.',
        path: 'macmute',
        category: 'Ứng dụng macOS',
        platform: 'macOS',
        status: 'live',
        tags: ['swift', 'macos', 'menubar', 'microphone', 'opensource'],
        features: [
            'Phím tắt toàn cục ở bất kỳ đâu — mặc định ⌃⌥⌘M, đổi được',
            'Hold to Talk: giữ phím để nói, thả ra là tắt lại',
            'Tắt ở mức thiết bị nên phủ mọi app: Teams, Zoom, Meet, Slack, Discord',
            'Tắt mọi micro chứ không chỉ thiết bị mặc định của hệ thống',
            'Đồng bộ nút Mute trong Microsoft Teams để người khác thấy bạn đã tắt',
            'Miễn phí, mã nguồn mở MIT, macOS 13+',
        ],
        techStack: ['Swift', 'CoreAudio', 'Accessibility API'],
        date: '2025-05',
        thumbnail: '/macmute/thumbnail.png',
        images: ['/macmute/thumbnail.png'],
        githubUrl: 'https://github.com/TuanBT/MacMute',
        downloadUrl: 'https://github.com/TuanBT/MacMute/releases/latest',
        docsUrl: 'https://tuanbt.github.io/MacMute/',
    },
    {
        id: 'gitkrapro',
        title: 'GitKraPro',
        tagline: 'Công cụ dòng lệnh cho GitKraken',
        description: 'CLI viết bằng TypeScript, dùng cho mục đích phi thương mại',
        longDescription:
            'Một công cụ dòng lệnh viết bằng TypeScript, chạy trên Node 18 trở lên, ' +
            'dành cho mục đích phi thương mại. Chi tiết cách dùng nằm trong README của repo.',
        path: 'gitkrapro',
        category: 'Tiện ích',
        platform: 'CLI',
        status: 'archived',
        tags: ['typescript', 'cli', 'nodejs'],
        features: [],
        techStack: ['TypeScript', 'Node.js'],
        date: '2024-03',
        thumbnail: '/gitkrapro/thumbnail.png',
        images: ['/gitkrapro/thumbnail.png'],
        githubUrl: 'https://github.com/TuanBT/gitkrapro',
    },
    {
        id: 'bingwallpaper',
        title: 'Bing Wallpaper for Mac',
        tagline: 'Ảnh nền Bing mới mỗi ngày, ở 4K',
        description: 'Ứng dụng menu bar macOS tự tải ảnh Bing trong ngày ở 4K và đặt làm hình nền trên mọi màn hình',
        longDescription:
            'Microsoft có ứng dụng Bing Wallpaper nhưng chỉ cho Windows. Đây là bản tương đương cho máy Mac.\n' +
            'Mỗi ngày ứng dụng tải ảnh của ngày từ Bing ở độ phân giải 3840×2160 và đặt làm hình nền — ' +
            'trên tất cả màn hình và tất cả Space, không chỉ màn hình đang mở. Nó nằm im trên thanh menu, ' +
            'không có icon dưới Dock, bản cài chỉ 2,5 MB và chạy native trên cả Apple Silicon lẫn Intel.\n' +
            'Bing đăng ảnh khác nhau theo từng quốc gia; bạn chọn được một trong 55 vùng, hoặc xem tạm ảnh ' +
            'của vùng khác rồi quay lại vùng của mình.',
        path: 'bingwallpaper',
        category: 'Ứng dụng macOS',
        platform: 'macOS',
        status: 'live',
        tags: ['swift', 'macos', 'wallpaper', 'menubar', 'opensource'],
        features: [
            'Ảnh trong ngày ở 4K (3840×2160), áp lên mọi màn hình và mọi Space',
            '55 vùng của Bing — xem tạm vùng khác rồi quay lại vùng của mình',
            'Hai kiểu hẹn giờ: kiểm tra mỗi N tiếng, hoặc đúng một giờ cố định mỗi ngày',
            'Tự dọn ảnh cũ theo hạn 1 / 2 / 5 / 10 ngày, hoặc giữ mãi',
            'Universal binary 2,5 MB, không icon Dock, mở cùng hệ thống',
            'Miễn phí và mã nguồn mở, macOS 11+',
        ],
        techStack: ['Swift', 'AppKit', 'AppleScript'],
        date: '2025-04',
        thumbnail: '/bingwallpaper/thumbnail.png',
        images: ['/bingwallpaper/thumbnail.png'],
        githubUrl: 'https://github.com/TuanBT/BingWallpaper-for-Mac',
        downloadUrl: 'https://github.com/TuanBT/BingWallpaper-for-Mac/releases/latest',
        docsUrl: 'https://tuanbt.github.io/BingWallpaper-for-Mac/',
    },
];

export default seedApps;
